import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Hero from './ui/Hero';
import Notification from './ui/Notification';
import '../styles/ProgramOverview.css';
import BuyingEVIcon from './icons/BuyingEVIcon';
import CheckmarkListItem from './ui/CheckmarkListItem';
import AlreadyHaveEVIcon from './icons/AlreadyHaveEVIcon';
import MailIcon from './icons/MailIcon';
import ExternalLinkIcon from './icons/ExternalLinkIcon';
import TermsAndConditions from './TermsAndConditions';

const ProgramOverview = ({ ...props }) => {
  const params = new URLSearchParams(window.location.search);
  const initialLang = params.get('lang');
  const [language, setLanguage] = useState(initialLang === 'fr' ? 'fr' : 'en');
  const [backgroundImage, setBackgroundImage] = useState(
    `/wp-content/plugins/ev-incentive-react/build/images/man-plugging-in-car-blur-background.png`
  );
  const [heroImage, setHeroImage] = useState(
    `/wp-content/plugins/ev-incentive-react/build/images/man-plugging-in-EV-header.png`
  );

  const [showOptions, setShowOptions] = useState(false);

  const iconVariant = {
    hidden: { rotate: 0, transition: { duration: 0.2 } },
    visible: { rotate: 180, transition: { duration: 0.2 } },
  };

  const slideFadeButtonVariant = {
    hidden: {
      opacity: 0,
      transition: {
        opacity: { duration: 0.2, ease: 'easeInOut' },
      },
    },
    visible: {
      opacity: 1,
      transition: {
        opacity: { duration: 0.2, ease: 'easeInOut' },
      },
    },
    exit: {
      opacity: 0,
      transition: { opacity: { duration: 0.2, ease: 'easeInOut' } },
    },
  };

  const languages = {
    en: {
      heroTitle: 'Manitoba Electric Vehicle Rebate Program',
      heroSubheader:
        'Take advantage of this rebate and make the eco-friendly choice today!',
      notification: "The Government of Manitoba has announced an extension of the Electric Vehicle (EV) Rebate Program beyond March 31, 2026. More information about the extended program will be provided when available.",
      preambleOne:
        'Manitobans are looking to make the switch to electric or plug-in hybrid electric vehicles (EVs). Whether you’re purchasing or leasing an EV, the <strong>Manitoba Electric Vehicle Rebate Program</strong> offers substantial savings to make the transition to green transportation more affordable. ',
      rebatesTitle: 'What are the rebates?',
      rebatesText:
        'Eligible EVs that are purchased or leased on or <strong>after August 1, 2023</strong> will receive the following rebates:',
      newTitle: 'New EVs:',
      newPrice: '$4,000',
      leasedTitle: 'Leased EVs:',
      leasedPrice: '$625 to $4,000',
      leasedSecondary: 'See FAQ for details.',
      preOwnedTitle: 'Pre-owned EVs:',
      preOwnedPrice: '$2,500',
      howProgramWorks: 'How the program works',
      eligibilityCriteria: 'Eligibility criteria',
      eligibilityPreamble:
        'You may be <strong>eligible for the EV rebate</strong>, based on the following criteria:',
      eligibleParticipants: 'Eligible participants',
      eligibleParticipantsOne:
        'Permanent Manitoba residents, corporations, businesses, First Nations, and municipalities.',
      eligibleParticipantsTwo:
        'Only one rebate per vehicle/person or organization is allowed during the program.',
      eligibleParticipantsThree:
        'Rebates are available on a first-come, first-served basis.',
      vehicleRequirements: 'Vehicle requirements',
      vehicleRequirementsOne:
        'New EVs with a Manufacturer’s Suggested Retail Price below $70,000.',
      vehicleRequirementsTwo:
        'Pre-owned EVs with a purchase price below $70,000.',
      vehicleRequirementsThree:
        'Vehicles must be purchased from an authorized dealership in Manitoba. ',
      vehicleRequirementsFour: 'Vehicles must be four model years old or less. ',
      howToApply: 'How to apply',
      appProcess: 'The application process is as simple as 1, 2, 3:',
      stepOne: 'Step 1',
      stepOneText:
        'Purchase/lease an eligible EV from an authorized Manitoba dealership. ',
      stepTwo: 'Step 2',
      stepTwoText: 'Register your EV with MPI.',
      stepThree: 'Step 3',
      stepThreeText:
        'Apply online by submitting your application through the MPI website.',
      postApproval:
        'If your application is approved, you will receive a rebate cheque <strong>by mail within 60 days</strong>. ',
      getStarted: 'Get started with your application',
      getStartedPreamble:
        "See if you're eligible for the EV Rebate by selection one of these options:",
      thinkingAbout: 'Thinking about purchasing or leasing an EV?',
      checkEligibility: 'Check your eligibility before you purchase or lease. ',
      checkEligibilityButton: 'Check eligibility',
      buttonOne: 'Check vehicle by VIN',
      buttonTwo: 'Check vehicle by model',
      buttonThree: 'Check customer account',
      alreadyHave: 'Already have your EV?',
      alreadyHavePreamble: 'Submit an application online for the EV Rebate.',
      applyToday: 'Apply today',
      alreadyApplied: 'Already applied for the EV Rebate?',
      checkStatus: 'Check the status of your online application.',
      checkStatusButton: 'Check status',
      questions: 'Questions?',
      questionsPreamble:
        "Visit the FAQ page on the Province of Manitoba's website. If you still have questions, email <a href='mailto:evrebate@mpi.mb.ca'>evrebate@mpi.mb.ca</a> and we'll be happy to help!",
      faq: 'FAQs',
      longTerm:
        'EVs offer long-term cost savings and environmental benefits. The Manitoba Electric Vehicle Rebate program makes it more appealing to make the switch.',
    },
    fr: {
      heroTitle:
        'Programme de remboursement à l’achat ou à la location d’un véhicule électrique du Manitoba',
      heroSubheader:
        'Profitez de ce remboursement et faites le choix écologique dès aujourd’hui!',
      notification: "Le gouvernement du Manitoba a annoncé la prolongation du programme de rebais pour les véhicules électriques (VE) au-delà du 31 mars 2026. Plus d’informations sur le programme étendu seront fournies lorsqu’elles seront disponibles.",
      preambleOne:
        'Les Manitobains souhaitent passer aux véhicules électriques (VE) ou aux véhicules hybrides électriques rechargeables. Que vous achetiez ou louiez un véhicule électrique, le <strong>Programme de remboursement à l’achat ou à la location d’un véhicule électrique</strong> offre des économies substantielles pour rendre plus abordable la transition vers un mode de transport écologique. ',
      rebatesTitle: 'Quels sont les remboursements?',
      rebatesText:
        'Les VE admissibles achetés ou loués <strong>le 1er août 2023 ou après</strong> bénéficieront des remboursements suivants :',
      newTitle: 'Nouveaux VE :',
      newPrice: '4 000$',
      leasedTitle: 'VE loués :',
      leasedPrice: 'De 625 $ à 4 000 $',
      leasedSecondary: 'voir la FAQ pour le détail.',
      preOwnedTitle: 'VE d’occasion :',
      preOwnedPrice: '2 500$',
      howProgramWorks: 'Fonctionnement du Programme',
      eligibilityCriteria: 'Critères d’admissibilité',
      eligibilityPreamble:
        'Vous pourriez <strong>être admissible pour bénéficier du remboursement sur les VE</strong> si vous remplissez les conditions suivantes :',
      eligibleParticipants: 'Participants admissibles',
      eligibleParticipantsOne:
        'Les résidents permanents, les compagnies, les entreprises, les Premières Nations et les municipalités du Manitoba..',
      eligibleParticipantsTwo:
        'Un seul remboursement par véhicule/personne ou organisation est autorisé pendant la durée du programme.',
      eligibleParticipantsThree:
        'Les remboursements sont accordés selon le principe du premier arrivé, premier servi.',
      vehicleRequirements: 'Exigences liées au véhicule',
      vehicleRequirementsOne:
        'Nouveaux VE dont le prix de détail suggéré par le fabricant est inférieur à 70 000 $.',
      vehicleRequirementsTwo:
        'Les VE d’occasion dont le prix d’achat est inférieur à 70 000 $.',
      vehicleRequirementsThree:
        'Les véhicules doivent être achetés auprès d’un concessionnaire autorisé au Manitoba. ',
      vehicleRequirementsFour:
        'Les véhicules doivent avoir quatre années-modèles ou moins. ',
      howToApply: 'Comment présenter une demande',
      appProcess: 'La procédure de demande est simple comme tout :',
      stepOne: 'Étape 1',
      stepOneText:
        'Achetez ou louez un VE admissible auprès d’un concessionnaire autorisé du Manitoba. ',
      stepTwo: 'Étape 2',
      stepTwoText: 'Enregistrez votre VE auprès de la SAPM.',
      stepThree: 'Étape 3',
      stepThreeText:
        'Faites votre demande en ligne en soumettant votre candidature sur le site Web de la SAPM.',
      postApproval:
        'Si votre demande est approuvée, vous recevrez un chèque de remboursement <strong>par courrier dans les 60 jours.</strong> ',
      getStarted: 'Commencez à remplir votre demande!',
      getStartedPreamble:
        'Vérifiez si vous avez droit au remboursement à l’achat d’un VE en sélectionnant l’une des options suivantes :',
      thinkingAbout:
        'Vous envisagez d’acheter ou de louer un véhicule électrique?',
      checkEligibility:
        'Vérifiez votre admissibilité avant d’acheter ou de louer. ',
      checkEligibilityButton: 'Vérifier l’admissibilité ',
      buttonOne: 'Vérifier le véhicule par NIV',
      buttonTwo: 'Vérifier le véhicule par modèle',
      buttonThree: 'Vérifier le compte du client',
      alreadyHave: 'Vous avez déjà votre VE?',
      alreadyHavePreamble:
        'Soumettez une demande en ligne pour le remboursement sur les VE. ',
      applyToday: 'Faites une demande aujourd’hui',
      alreadyApplied:
        'Vous avez déjà fait une demande de remboursement pour un VE?',
      checkStatus: 'Vérifiez l’état de votre demande en ligne. ',
      checkStatusButton: 'Vérifier l’état',
      questions: 'Questions?',
      questionsPreamble:
        "Consultez la page FAQ sur le site Web de la province du Manitoba. Si vous avez encore des questions, écrivez-nous à <a href='mailto:evrebate@mpi.mb.ca'>evrebate@mpi.mb.ca</a> et nous nous ferons un plaisir de vous aider!",
      faq: 'FAQ',
      longTerm:
        'Les VE permettent de réaliser des économies à long terme et présentent des avantages pour l’environnement. Le Programme de remboursement à l’achat ou à la location d’un véhicule électrique rend la transition plus attrayante.',
    },
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 600) {
        setHeroImage(
          `/wp-content/plugins/ev-incentive-react/build/images/man-plugging-in-car-mobile.png`
        );
      } else {
        setHeroImage(
          `/wp-content/plugins/ev-incentive-react/build/images/man-plugging-in-EV-header.png`
        );
      }
    };

    window.addEventListener('resize', handleResize);

    // Call handler once to set initial state
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <Hero background={`${heroImage}`}>
        <h1>{languages[language].heroTitle}</h1>
        <h3 className="subheader">{languages[language].heroSubheader}</h3>
      </Hero>

      <section className="title-section">
        <div className="container">
          <Notification type="info" useIcon={false} >{languages[language].notification}</Notification>
          <p
            dangerouslySetInnerHTML={{
              __html: languages[language].preambleOne,
            }}
          ></p>
          <div className="incentives">
            <h2>{languages[language].rebatesTitle}</h2>
            <p
              dangerouslySetInnerHTML={{
                __html: languages[language].rebatesText,
              }}
            ></p>
            <div>
              <div>
                <div className="incentive-header">
                  <span>{languages[language].newTitle}</span>
                </div>
                <div className="incentive-body">
                  <p>{languages[language].newPrice}</p>
                </div>
              </div>
              <div>
                <div className="incentive-header">
                  <span>{languages[language].leasedTitle}</span>
                </div>
                <div className="incentive-body">
                  <p>{languages[language].leasedPrice}</p>
                  <span>
                    <a
                      href="https://www.gov.mb.ca/lowercosts/evrebate/index.html#faq"
                      target="_blank"
                    >
                      {languages[language].leasedSecondary}
                    </a>
                  </span>
                </div>
              </div>
              <div>
                <div className="incentive-header">
                  <span>{languages[language].preOwnedTitle}</span>
                </div>
                <div className="incentive-body">
                  <p>{languages[language].preOwnedPrice}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section>
        <div className="container">
          <h2 style={{ textAlign: 'center' }}>
            {languages[language].howProgramWorks}
          </h2>
          <div className="grid-2 outer-grid">
            <div>
              <h4>{languages[language].eligibilityCriteria}</h4>
              <h5
                dangerouslySetInnerHTML={{
                  __html: languages[language].eligibilityPreamble,
                }}
              ></h5>
              <div className="grid-2">
                <div>
                  <h6>{languages[language].eligibleParticipants}</h6>
                  <CheckmarkListItem>
                    {languages[language].eligibleParticipantsOne}
                  </CheckmarkListItem>
                  <CheckmarkListItem>
                    {languages[language].eligibleParticipantsTwo}
                  </CheckmarkListItem>
                  <CheckmarkListItem>
                    {languages[language].eligibleParticipantsThree}
                  </CheckmarkListItem>
                </div>
                <div>
                  <h6>{languages[language].vehicleRequirements}</h6>
                  <CheckmarkListItem>
                    {languages[language].vehicleRequirementsOne}
                  </CheckmarkListItem>
                  <CheckmarkListItem>
                    {languages[language].vehicleRequirementsTwo}
                  </CheckmarkListItem>
                  <CheckmarkListItem>
                    {languages[language].vehicleRequirementsThree}
                  </CheckmarkListItem>
                  <CheckmarkListItem>
                    {languages[language].vehicleRequirementsFour}
                  </CheckmarkListItem>
                </div>
              </div>
            </div>
            <div>
              <h4>{languages[language].howToApply}</h4>
              <h5>{languages[language].appProcess}</h5>
              <div className="step-section">
                <div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                  >
                    <g id="Frame">
                      <path
                        id="Vector"
                        d="M23 9.75H21.9875L19.3831 3.89062C19.2653 3.62553 19.0731 3.40029 18.8298 3.24222C18.5865 3.08414 18.3026 3 18.0125 3H6.9875C6.69738 3 6.41349 3.08414 6.17022 3.24222C5.92695 3.40029 5.73474 3.62553 5.61688 3.89062L3.0125 9.75H2C1.80109 9.75 1.61032 9.82902 1.46967 9.96967C1.32902 10.1103 1.25 10.3011 1.25 10.5C1.25 10.6989 1.32902 10.8897 1.46967 11.0303C1.61032 11.171 1.80109 11.25 2 11.25H2.75V18.75C2.75 19.1478 2.90804 19.5294 3.18934 19.8107C3.47064 20.092 3.85218 20.25 4.25 20.25H6.5C6.89782 20.25 7.27936 20.092 7.56066 19.8107C7.84196 19.5294 8 19.1478 8 18.75V17.25H17V18.75C17 19.1478 17.158 19.5294 17.4393 19.8107C17.7206 20.092 18.1022 20.25 18.5 20.25H20.75C21.1478 20.25 21.5294 20.092 21.8107 19.8107C22.092 19.5294 22.25 19.1478 22.25 18.75V11.25H23C23.1989 11.25 23.3897 11.171 23.5303 11.0303C23.671 10.8897 23.75 10.6989 23.75 10.5C23.75 10.3011 23.671 10.1103 23.5303 9.96967C23.3897 9.82902 23.1989 9.75 23 9.75ZM6.9875 4.5H18.0125L20.3459 9.75H4.65406L6.9875 4.5ZM6.5 18.75H4.25V17.25H6.5V18.75ZM18.5 18.75V17.25H20.75V18.75H18.5ZM20.75 15.75H4.25V11.25H20.75V15.75ZM5.75 13.5C5.75 13.3011 5.82902 13.1103 5.96967 12.9697C6.11032 12.829 6.30109 12.75 6.5 12.75H8C8.19891 12.75 8.38968 12.829 8.53033 12.9697C8.67098 13.1103 8.75 13.3011 8.75 13.5C8.75 13.6989 8.67098 13.8897 8.53033 14.0303C8.38968 14.171 8.19891 14.25 8 14.25H6.5C6.30109 14.25 6.11032 14.171 5.96967 14.0303C5.82902 13.8897 5.75 13.6989 5.75 13.5ZM16.25 13.5C16.25 13.3011 16.329 13.1103 16.4697 12.9697C16.6103 12.829 16.8011 12.75 17 12.75H18.5C18.6989 12.75 18.8897 12.829 19.0303 12.9697C19.171 13.1103 19.25 13.3011 19.25 13.5C19.25 13.6989 19.171 13.8897 19.0303 14.0303C18.8897 14.171 18.6989 14.25 18.5 14.25H17C16.8011 14.25 16.6103 14.171 16.4697 14.0303C16.329 13.8897 16.25 13.6989 16.25 13.5Z"
                        fill="#383838"
                      />
                    </g>
                  </svg>
                  <span>{languages[language].stepOne}</span>
                </div>
                <p>{languages[language].stepOneText}</p>
              </div>
              <div className="step-section">
                <div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                  >
                    <g id="Frame">
                      <path
                        id="Vector"
                        d="M9.5 12.75L11.75 15L15.5 9.75M21.5 12C21.5 13.268 20.87 14.39 19.907 15.068C20.0108 15.6608 19.9701 16.2698 19.7886 16.8436C19.607 17.4173 19.2899 17.9388 18.864 18.364C18.4388 18.7899 17.9173 19.107 17.3436 19.2886C16.7698 19.4701 16.1608 19.5108 15.568 19.407C15.2222 19.8995 14.7629 20.3014 14.2288 20.5787C13.6948 20.856 13.1017 21.0005 12.5 21C11.232 21 10.11 20.37 9.432 19.407C8.83923 19.5107 8.23021 19.47 7.65649 19.2885C7.08276 19.1069 6.56122 18.7898 6.136 18.364C5.71013 17.9388 5.39298 17.4173 5.21142 16.8436C5.02987 16.2698 4.98925 15.6608 5.093 15.068C4.60052 14.7222 4.19862 14.2629 3.92133 13.7288C3.64403 13.1948 3.49951 12.6017 3.5 12C3.5 10.732 4.13 9.61 5.093 8.932C4.98925 8.33923 5.02987 7.73019 5.21142 7.15645C5.39298 6.58271 5.71013 6.06117 6.136 5.636C6.56122 5.21019 7.08276 4.8931 7.65649 4.71154C8.23021 4.52999 8.83923 4.48933 9.432 4.593C9.77785 4.10058 10.2372 3.69873 10.7713 3.42144C11.3053 3.14415 11.8983 2.99959 12.5 3C13.768 3 14.89 3.63 15.568 4.593C16.1608 4.48933 16.7698 4.52999 17.3435 4.71154C17.9172 4.8931 18.4388 5.21019 18.864 5.636C19.2898 6.06122 19.6069 6.58276 19.7885 7.15649C19.97 7.73021 20.0107 8.33923 19.907 8.932C20.3995 9.27779 20.8014 9.73715 21.0787 10.2712C21.356 10.8052 21.5005 11.3983 21.5 12Z"
                        stroke="#383838"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </g>
                  </svg>
                  <span>{languages[language].stepTwo}</span>
                </div>
                <p>{languages[language].stepTwoText}</p>
              </div>
              <div className="step-section">
                <div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                  >
                    <path
                      d="M8.75006 2.24994V1.49994C8.75006 1.30103 8.82908 1.11026 8.96973 0.96961C9.11038 0.828957 9.30115 0.74994 9.50006 0.74994C9.69897 0.74994 9.88974 0.828957 10.0304 0.96961C10.171 1.11026 10.2501 1.30103 10.2501 1.49994V2.24994C10.2501 2.44885 10.171 2.63962 10.0304 2.78027C9.88974 2.92092 9.69897 2.99994 9.50006 2.99994C9.30115 2.99994 9.11038 2.92092 8.96973 2.78027C8.82908 2.63962 8.75006 2.44885 8.75006 2.24994ZM2.00006 9.74994H2.75006C2.94897 9.74994 3.13974 9.67092 3.28039 9.53027C3.42104 9.38962 3.50006 9.19885 3.50006 8.99994C3.50006 8.80103 3.42104 8.61026 3.28039 8.46961C3.13974 8.32896 2.94897 8.24994 2.75006 8.24994H2.00006C1.80115 8.24994 1.61038 8.32896 1.46973 8.46961C1.32908 8.61026 1.25006 8.80103 1.25006 8.99994C1.25006 9.19885 1.32908 9.38962 1.46973 9.53027C1.61038 9.67092 1.80115 9.74994 2.00006 9.74994ZM12.1644 3.67119C12.2526 3.71533 12.3485 3.74166 12.4469 3.74868C12.5452 3.7557 12.6439 3.74327 12.7374 3.7121C12.831 3.68093 12.9174 3.63163 12.9919 3.56702C13.0663 3.50241 13.1273 3.42376 13.1713 3.33556L13.9213 1.83556C14.0103 1.65766 14.025 1.45169 13.9622 1.26295C13.8993 1.07421 13.7641 0.918172 13.5862 0.829158C13.4083 0.740145 13.2023 0.725449 13.0135 0.788303C12.8248 0.851157 12.6688 0.986412 12.5798 1.16431L11.8297 2.66431C11.7856 2.7524 11.7592 2.84832 11.7521 2.9466C11.745 3.04488 11.7574 3.1436 11.7885 3.2371C11.8195 3.33061 11.8687 3.41708 11.9333 3.49156C11.9978 3.56604 12.0763 3.62708 12.1644 3.67119ZM3.16444 11.3296L1.66444 12.0796C1.48653 12.1686 1.35128 12.3247 1.28843 12.5134C1.22557 12.7022 1.24027 12.9081 1.32928 13.086C1.41829 13.2639 1.57433 13.3992 1.76307 13.462C1.95181 13.5249 2.15778 13.5102 2.33569 13.4212L3.83569 12.6712C3.92378 12.6271 4.00232 12.5661 4.06684 12.4917C4.13135 12.4173 4.18058 12.3309 4.2117 12.2374C4.24282 12.1439 4.25523 12.0453 4.24822 11.947C4.24121 11.8488 4.21492 11.7529 4.17084 11.6648C4.12677 11.5767 4.06577 11.4982 3.99134 11.4336C3.91691 11.3691 3.83051 11.3199 3.73705 11.2888C3.6436 11.2576 3.54493 11.2452 3.44668 11.2522C3.34843 11.2593 3.25252 11.2856 3.16444 11.3296ZM21.0604 17.2499C21.1997 17.3892 21.3102 17.5546 21.3856 17.7366C21.461 17.9186 21.4998 18.1137 21.4998 18.3107C21.4998 18.5077 21.461 18.7028 21.3856 18.8848C21.3102 19.0668 21.1997 19.2322 21.0604 19.3715L19.8716 20.5603C19.7323 20.6996 19.567 20.8101 19.3849 20.8855C19.2029 20.9609 19.0079 20.9997 18.8108 20.9997C18.6138 20.9997 18.4187 20.9609 18.2367 20.8855C18.0547 20.8101 17.8894 20.6996 17.7501 20.5603L12.9407 15.7499L11.2813 20.0709C11.2813 20.0803 11.2738 20.0906 11.2691 20.1009C11.1543 20.3685 10.9634 20.5964 10.72 20.7563C10.4766 20.9162 10.1916 21.0009 9.90037 20.9999H9.82631C9.52263 20.9871 9.23021 20.8813 8.9886 20.6968C8.74699 20.5124 8.56784 20.2582 8.47537 19.9687L3.57506 4.96119C3.49094 4.69872 3.48076 4.41816 3.54563 4.15028C3.61051 3.88241 3.74794 3.63759 3.94283 3.4427C4.13772 3.24781 4.38253 3.11039 4.65041 3.04551C4.91828 2.98063 5.19885 2.99082 5.46131 3.07494L20.4688 7.97525C20.7556 8.0712 21.0068 8.25162 21.1892 8.49282C21.3717 8.73403 21.477 9.02477 21.4913 9.32688C21.5056 9.62899 21.4282 9.92836 21.2693 10.1857C21.1104 10.4431 20.8775 10.6464 20.601 10.769L20.571 10.7812L16.2501 12.4396L21.0604 17.2499ZM20.0001 18.3103L15.1897 13.4999C15.0166 13.3271 14.8884 13.1146 14.8162 12.8808C14.744 12.6471 14.7301 12.3992 14.7757 12.1589C14.8213 11.9185 14.9249 11.6929 15.0777 11.5018C15.2304 11.3107 15.4275 11.1599 15.6519 11.0624L15.6819 11.0493L19.9897 9.39557L5.00006 4.49994L9.89381 19.4868L11.5485 15.1743C11.5485 15.164 11.556 15.1537 11.5607 15.1434C11.6582 14.9192 11.809 14.7222 12 14.5696C12.191 14.4169 12.4164 14.3133 12.6566 14.2678C12.7494 14.2506 12.8435 14.2418 12.9379 14.2415C13.3353 14.2419 13.7163 14.4001 13.9972 14.6812L18.8104 19.4999L20.0001 18.3103Z"
                      fill="black"
                    />
                  </svg>
                  <span>{languages[language].stepThree}</span>
                </div>
                <p>{languages[language].stepThreeText}</p>
              </div>
              <p
                dangerouslySetInnerHTML={{
                  __html: languages[language].postApproval,
                }}
                style={{ marginTop: '2.5rem' }}
              ></p>
            </div>
          </div>
        </div>
      </section>
      <section
        className="blur-bg"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        <div className="container">
          <div className="get-started">
            <h2>{languages[language].getStarted}</h2>
            <p style={{ marginBottom: '2.25rem' }}>
              {languages[language].getStartedPreamble}
            </p>
            <div className="card-columns">
              <div className="card">
                <div className="top">
                  <div>
                    <BuyingEVIcon />
                    <h3>{languages[language].thinkingAbout}</h3>
                  </div>
                  <div>
                    <p>{languages[language].checkEligibility}</p>
                    <button
                      className="primary"
                      onClick={() => {
                        setShowOptions((prevState) => !prevState);
                      }}
                      style={{ zIndex: 10, position: 'relative' }}
                    >
                      {languages[language].checkEligibilityButton}{' '}
                      <motion.svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        variants={iconVariant}
                        animate={showOptions ? 'visible' : 'hidden'}
                        style={{ marginLeft: '8px' }}
                      >
                        <path
                          d="M0.689033 8.311L2.81103 6.189L12 15.379L21.189 6.189L23.311 8.311L12 19.621L0.689033 8.311Z"
                          fill="#FFFFFF"
                        />
                      </motion.svg>
                    </button>
                    <AnimatePresence>
                      {showOptions && (
                        <motion.div
                          key="eligiblity-options"
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          variants={slideFadeButtonVariant}
                          transition={{ duration: 0.2, ease: 'easeInOut' }}
                          className="eligiblity-options"
                        >
                          <span
                            onClick={() => {
                              window.location.href = '/vehicle-eligibility';
                            }}
                          >
                            {languages[language].buttonOne}
                          </span>
                          <span
                            onClick={() => {
                              window.open(
                                'https://www.manitoba.ca/lowercosts/evrebate/eligible-vehicles.html',
                                '_blank'
                              );
                            }}
                          >
                            {languages[language].buttonTwo}
                          </span>
                          <span
                            onClick={() => {
                              window.location.href = '/customer-eligibility';
                            }}
                          >
                            {languages[language].buttonThree}
                          </span>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
              <div
                className="card"
                style={{ position: 'relative', zIndex: '5' }}
              >
                <div className="top">
                  <div>
                    <AlreadyHaveEVIcon />
                    <h3>{languages[language].alreadyHave}</h3>
                  </div>
                  <div>
                    <p>{languages[language].alreadyHavePreamble}</p>
                    <button
                      className="primary"
                      onClick={() => {
                        window.location.href = '/application-submission';
                      }}
                    >
                      {languages[language].applyToday}
                    </button>
                  </div>
                </div>
              </div>
              <div
                className="card"
                style={{ position: 'relative', zIndex: '6' }}
              >
                <div className="top">
                  <div>
                    <MailIcon />
                    <h3>{languages[language].alreadyApplied}</h3>
                  </div>
                  <div>
                    <p>{languages[language].checkStatus}</p>
                    <button
                      className="primary"
                      onClick={() => {
                        window.location.href = '/application-status';
                      }}
                    >
                      {languages[language].checkStatusButton}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="program-footer">
        <div className="container">
          <div>
            <div>
              <h2 className="margin-fix">{languages[language].questions}</h2>
              <p
                dangerouslySetInnerHTML={{
                  __html: languages[language].questionsPreamble,
                }}
                className="margin-fix"
              ></p>
              <button
                className="primary"
                onClick={() => {
                  window.open(
                    'https://www.gov.mb.ca/lowercosts/evrebate/index.html#faq',
                    '_blank'
                  );
                }}
              >
                <ExternalLinkIcon /> {languages[language].faq}
              </button>
            </div>
            <div>
              <img
                src={`/wp-content/plugins/ev-incentive-react/build/images/ev-plug-sunset-tree.png`}
                alt="EV Plug Sunset Tree"
              />
              <h4>{languages[language].longTerm}</h4>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ProgramOverview;
